import { React, useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import api from "../../api/axiosConfig.js";
import { ProjectContext } from "../../App";
import ProjectPieChart from "../Dashboard/ProjectPieChart.js";
import TimesheetPieChart from "../Dashboard/TimesheetPieChart.js";
import TimesheetCalendar from "../Dashboard/TimesheetCalendar.js";
import ProjectPhasePieChart from "../Dashboard/ProjectPhasePieChart.js";
import TimesheetDurationBillableNonBillable from "../Dashboard/TimesheetDurationBillableNonBillable.js";
import "../../styles/projects/Dashboard.css";

const fetchData = async (url, setData, setError) => {
  try {
    // Use api.get, which includes the base URL and interceptors
    const response = await api.get(url);
    setData(response.data); // Set the fetched data from response
  } catch (error) {
    setError(error); // Handle error
  }
};

const DashBoard = () => {
  const { dialBoxopen, isNavBarOpen } = useContext(ProjectContext);
  const { projectId } = useParams();
  const [project, setProject] = useState({
    id: null,
    name: "",
    projectno: "",
    customer_name: "",
    phases: [],
  });
  const [issues, setIssues] = useState([]);
  const [variations, setVariations] = useState([]);
  const [claimPercent, setClaimPercent] = useState(0);
  const [totalPlannedHours, setTotalPlannedHours] = useState("");
  const [bookedHours, setBookedHours] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timesheetData, setTimesheetData] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    const fetchAllData = async () => {
      if (!projectId) return;

      setLoading(true);

      // Use this to temporarily store fetched data
      let projectDataRef = null;

      await Promise.all([
        fetchData(
          `/api/project/${projectId}/phases`,
          async (data) => {
            setProject(data);
            projectDataRef = data;

            if (data.phases) {
              const totalPlannedHours = data.phases.reduce(
                (total, phase) => total + (phase.plannedhours || 0),
                0
              );
              // ✅ Overall average invoice status across all phases
              const totalInvoiceStatus = data.phases.reduce(
                (sum, phase) => sum + (phase.invoice_status || 0),
                0
              );

              const overallPercentage =
                data.phases.length > 0
                  ? (totalInvoiceStatus / data.phases.length).toFixed()
                  : 0;
              setTotalPlannedHours(totalPlannedHours);
              setClaimPercent(overallPercentage);

              // Fetch variations and issues for each phase
              const variationData = [];
              const issueData = [];

              await Promise.all(
                data.phases.map(async (phase) => {
                  await fetchData(
                    `/api/phase/${phase.id}/variations`,
                    (variations) => variationData.push(...(variations || [])),
                    (err) => console.error("Variation fetch error", err)
                  );
                  await fetchData(
                    `/api/phase/${phase.id}/issues`,
                    (issues) => issueData.push(...(issues || [])),
                    (err) => console.error("Issue fetch error", err)
                  );
                })
              );

              setVariations(variationData);
              setIssues(issueData);
            }
          },
          setError
        ),

        fetchData(
          `/api/timesheettable/projects/${projectId}`,
          (data) => setTimesheetData(data),
          setError
        ),

        fetchData(
          `/api/projects/${projectId}/users`,
          (data) => {
            const activeUsers = data.filter(
              (user) => user.status !== "Inactive"
            );
            setUsers(activeUsers);
          },
          setError
        ),
      ]);

      setLoading(false);
    };

    fetchAllData();
  }, [projectId]);

  useEffect(() => {
    if (Array.isArray(timesheetData)) {
      const bookedHours = timesheetData.reduce((total, entry) => {
        const [hours, minutes] = entry.duration.split(":").map(Number);
        return total + hours + minutes / 60; // Only hours and minutes
      }, 0);
      setBookedHours(bookedHours);
    }
  }, [timesheetData]);

  return (
    <>
      <Helmet>
        <title>Msuite - Projects</title>
      </Helmet>
      {!dialBoxopen && (
        <div
          className={
            isNavBarOpen
              ? "dashboard-main-container"
              : "dashboard-main-container-close"
          }
        >
          <div
            className={
              isNavBarOpen ? "dashboard-container" : "dashboard-container-close"
            }
          >
            <div className="container-one">
              <ProjectPieChart
                projectName={project.name}
                phases={project.phases}
                overallBudgetHours={project.overall_budget_hours}
                overallActualHours={project.overall_actual_hours}
                bookedHours={bookedHours}
              />
            </div>
            <div className="container-two">
              <TimesheetPieChart
                projectName={project.name}
                plannedHours={totalPlannedHours}
                timesheetData={timesheetData}
              />
            </div>
          </div>
          <div
            className={
              isNavBarOpen ? "dashboard-container" : "dashboard-container-close"
            }
          >
            <div className="container-one">
              <ProjectPhasePieChart
                totalPhases={project.phases.length}
                claimedPhases={claimPercent}
                projectName={project.name}
              />
            </div>
            <div className="container-two">
              <TimesheetDurationBillableNonBillable
                projectName={project.name}
                timesheetData={timesheetData}
              />
            </div>
          </div>
          <div
            className={
              isNavBarOpen
                ? "dashboard-container last-container"
                : "dashboard-container-close last-container-close"
            }
          >
            <div className="container-one">
              <TimesheetCalendar projectId={projectId} />
            </div>
            <div className="container-two">
              <h3
                style={{
                  textWrap: "nowrap",
                  fontSize: "15px",
                  position: "relative",
                  top: "-15px",
                  height: "20px",
                }}
              >
                Project members :{" "}
              </h3>
              {users.length === 0 && (
                <div className="duration-image-container">
                  <img
                    className="members-image"
                    style={{ top: "50px" }}
                    src="/AppLayout/Project-member.png"
                  />
                  <p style={{ marginLeft: "-87px" }}>
                    Project members not available for this project
                  </p>
                </div>
              )}
              <ul className="ul-dashboard">
                {users.map((user) => (
                  <li key={user.name} className="user-details-list">
                    <p
                      style={{ display: "flex", alignItems: "center" }}
                      className="user-list"
                    >
                      <span className="initial">
                        {user.name.charAt(user.name.length - 1).toUpperCase() +
                          user.name.charAt(0).toUpperCase()}
                      </span>
                      <span>{user.name}</span>
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DashBoard;
